import type { ExpandedState } from '@tanstack/react-table';

const EXPANDED_STATE_KEY = 'smart-offers-table-expanded-state';

/**
 * Type guard to validate that parsed data matches ExpandedState type.
 * ExpandedState can be:
 * - boolean (true = all expanded, false = all collapsed)
 * - Record<string, boolean> (object with row IDs as keys and boolean values)
 */
function isValidExpandedState(value: unknown): value is ExpandedState {
  // Check if it's a boolean (both true and false are valid)
  if (typeof value === 'boolean') {
    return true;
  }

  // Check if it's a valid object (not null, not array)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // Validate that all values in the object are booleans
    return Object.values(value).every((v) => typeof v === 'boolean');
  }

  return false;
}

export class TableExpandedStateManager {
  static getExpandedState(): ExpandedState {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const stored = localStorage.getItem(EXPANDED_STATE_KEY);
      if (!stored) {
        return {};
      }

      const parsed = JSON.parse(stored);

      // Validate the parsed data matches ExpandedState type
      if (!isValidExpandedState(parsed)) {
        console.warn(
          'Invalid expanded state format in localStorage, resetting to default'
        );
        // Clear invalid data
        localStorage.removeItem(EXPANDED_STATE_KEY);
        return {};
      }

      return parsed;
    } catch (error) {
      console.error('Error reading expanded state from localStorage:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(EXPANDED_STATE_KEY);
      } catch (clearError) {
        console.error('Error clearing corrupted expanded state:', clearError);
      }
      return {};
    }
  }

  static setExpandedState(expandedState: ExpandedState): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(EXPANDED_STATE_KEY, JSON.stringify(expandedState));
    } catch (error) {
      console.error('Error writing expanded state to localStorage:', error);
    }
  }

  static clearExpandedState(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(EXPANDED_STATE_KEY);
    } catch (error) {
      console.error('Error clearing expanded state from localStorage:', error);
    }
  }
}
